import { css } from '@emotion/css';
import { make as RichText } from '@prezzee/ribbon/src/components/personal/RichText/RichText.gen';
import { CustomerCareBanner, Asset } from 'api/graphql/generated';
import CmsImage from 'components/CmsImage';

import { Media } from '../../../bindings/Media';
import { Color } from '../../../css/theme';
import useSmartLink from 'hooks/useSmartLink';

interface Props {
  bannerData: CustomerCareBanner;
}

const Styles = {
  container: (backgroundColor: string) => css`
    background-color: ${backgroundColor};
    display: flex;
    flex-direction: row;
    justify-content: end;
    @media (max-width: 599px) {
      flex-direction: column;
    }
  `,
  textContainer: css`
    display: flex;
    flex-direction: column;
    width: 50%;
    align-items: center;
    justify-content: center;
    margin-left: 8%;
    max-width: 700px;
    margin-left: 2rem;
    div {
      width: 100%;
    }
    @media (max-width: 640px) {
      margin-top: 3rem;
      margin-bottom: 3rem;
    }
    @media (max-width: 599px) {
      width: 90%;
      margin: 2rem auto;
    }
  `,
  title: css`
    font-family: DM Serif Display;
    font-style: italic;
    font-size: 4rem;
    line-height: 4.5rem;
    width: 100%;
    color: ${Color.Purple500};
    @media (max-width: 1288px) {
      font-size: 3rem;
      line-height: 3.5rem;
      margin-bottom: 0.5rem;
    }
  `,
  text: css`
    color: ${Color.Gray800};
    margin-top: 0.5rem;
    @media (max-width: 1024px) {
      margin: 0;
    }
    @media (max-width: 599px) {
      margin-top: 0;
      margin-bottom: 3rem;
    }
  `,
  imageContainer: css`
    display: flex;
    justify-content: end;
    width: 50%;
    div {
      width: 100%;
      max-width: 800px;
    }
    @media (max-width: 599px) {
      width: 100%;
    }
    @media (min-width: 600px) and (max-width: 611px) {
      width: 60%;
    }
  `,
  image: css`
    width: 100%;
    @media (max-width: 640px) {
      height: 100%;
    }
  `,
};
const CCBanner = ({ bannerData }: Props) => {
  const { title, text, backgroundColour, bannerAssetDesktop, bannerAssetTablet, bannerAssetMobile, _system_ } =
    bannerData;
  const spotlightProps = useSmartLink(_system_);

  const mobile = '(max-width: 599px)';
  const desktopLg = '(max-width: 1600px)';

  const createBannerImage = (imageObject: Asset, sizes: string) => {
    const file = imageObject;
    if (!file) return;

    const description = file?.description || 'bannerImage';
    const url = file?.url || '';
    const width = file?.width || 0;
    const height = file?.height || 0;

    return (
      <CmsImage src={url} alt={description} width={width} height={height} className={Styles.image} sizes={sizes} />
    );
  };

  const tabletDesktopBannerImage = (
    <>
      <Media between={['mobile', 'lg']}>{createBannerImage(bannerAssetTablet, '50vw')}</Media>
      <Media greaterThanOrEqual="lg">{createBannerImage(bannerAssetDesktop, `${desktopLg} 50vw, 800px`)}</Media>
    </>
  );

  return (
    <div className={Styles.container(backgroundColour)} {...spotlightProps}>
      <>
        <Media lessThan="mobile">
          <div className={Styles.imageContainer}>{createBannerImage(bannerAssetMobile, '100vw')}</div>
        </Media>
      </>
      <div className={Styles.textContainer}>
        <h1 className={Styles.title}>{title}</h1>
        <RichText className={Styles.text}>{text.html}</RichText>
      </div>
      <div className={Styles.imageContainer}>{tabletDesktopBannerImage}</div>
    </div>
  );
};

export default CCBanner;
